// ToastContext - transient toast notifications (success/error/info, auto-dismiss).

import { AlertCircle, CheckCircle2, Info, X } from 'lucide-react';
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { cn } from '@/lib/classnames';
import { useTopLayerHost } from '@/lib/toplayer';

type ToastKind = 'success' | 'error' | 'info';
interface Toast {
  id: number;
  kind: ToastKind;
  message: string;
}

interface ToastApi {
  push: (kind: ToastKind, message: string) => void;
  success: (m: string) => void;
  error: (m: string) => void;
  info: (m: string) => void;
}

const Ctx = createContext<ToastApi | null>(null);

/** How long a toast stays up before it retires itself. */
const DISMISS_MS = 4500;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  // Every pending dismissal, so unmounting cancels them.
  //
  // A toast schedules its own removal four and a half seconds out, and the
  // provider can be gone well before then - a route change, a logout, a test
  // file finishing. The timer still fires, calls setState on a tree that no
  // longer exists, and in an environment being torn down around it reaches for
  // globals that have already gone.
  const timers = useRef(new Set<ReturnType<typeof setTimeout>>());

  useEffect(
    () => () => {
      for (const timer of timers.current) clearTimeout(timer);
      timers.current.clear();
    },
    [],
  );

  const push = useCallback((kind: ToastKind, message: string) => {
    const id = Date.now() + Math.random();
    setToasts((t) => [...t, { id, kind, message }]);
    const timer = setTimeout(() => {
      timers.current.delete(timer);
      setToasts((t) => t.filter((x) => x.id !== id));
    }, DISMISS_MS);
    timers.current.add(timer);
  }, []);
  const api = useMemo<ToastApi>(
    () => ({
      push,
      success: (m) => push('success', m),
      error: (m) => push('error', m),
      info: (m) => push('info', m),
    }),
    [push]
  );
  // The stack renders into the open dialog when there is one. A toast is
  // raised from inside a dialog all the time - the one-time API key's
  // "Copied", a claim code, a save that failed - and on the z-index this
  // replaces every one of those painted under the dialog's backdrop, visible
  // to nobody and impossible to dismiss. The host follows the dialog rather
  // than being read once at push time, so the ordinary case of raising a
  // toast and closing the dialog together leaves the toast on screen.
  const host = useTopLayerHost() ?? document.body;

  return (
    <Ctx.Provider value={api}>
      {children}
      {createPortal(
        <div className="toast-stack">
          {toasts.map((t) => (
            <ToastView key={t.id} toast={t} onClose={() => setToasts((s) => s.filter((x) => x.id !== t.id))} />
          ))}
        </div>,
        host
      )}
    </Ctx.Provider>
  );
}

function ToastView({ toast, onClose }: { toast: Toast; onClose: () => void }) {
  const Icon = toast.kind === 'success' ? CheckCircle2 : toast.kind === 'error' ? AlertCircle : Info;
  return (
    <div
      className={cn(
        'pointer-events-auto flex items-start gap-3 p-3.5 rounded-xl border shadow-soft animate-slide-in-right',
        toast.kind === 'success' && 'border-verdict-clean/40 bg-ink-900/95',
        toast.kind === 'error' && 'border-verdict-malicious/40 bg-ink-900/95',
        toast.kind === 'info' && 'border-ink-700 bg-ink-900/95'
      )}
      role="alert"
    >
      <Icon
        className={cn(
          'shrink-0 w-5 h-5 mt-0.5',
          toast.kind === 'success' && 'text-verdict-clean',
          toast.kind === 'error' && 'text-verdict-malicious',
          toast.kind === 'info' && 'text-accent-300'
        )}
      />
      <p className="flex-1 text-sm text-ink-100">{toast.message}</p>
      <button
        onClick={onClose}
        className="text-ink-500 hover:text-ink-200 transition-colors -mr-1 -mt-1 p-1"
        aria-label="Dismiss"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}

export function useToast() {
  const v = useContext(Ctx);
  if (!v) throw new Error('useToast outside ToastProvider');
  return v;
}
